import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/components/ui/toast"
import {
  useAddCashRegisterMutation,
  useCashRegistersQuery,
} from "@/query/cashRegisterQuery"
import { useStripeReadersQuery } from "@/query/stripeTerminalQuery"
import type { CashRegisterTable } from "@okeys/shared-utils"
import { fetchUserAttributes } from "aws-amplify/auth"
import { Info, Loader2, Plus, Terminal, Wifi, WifiOff } from "lucide-react"
import React, { useState } from "react"

type AddCashRegisterModalProps = {
  isOpen: boolean
  onClose: () => void
  onOpenManageReaders: () => void
}

export const AddCashRegisterModal: React.FC<AddCashRegisterModalProps> = ({
  isOpen,
  onClose,
  onOpenManageReaders,
}) => {
  const addCashRegisterMutation = useAddCashRegisterMutation()
  const { addToast } = useToast()

  // États pour la caisse
  const [cashRegisterName, setCashRegisterName] = useState("")
  const [cashRegisterStatus, setCashRegisterStatus] = useState<
    "active" | "inactive"
  >("active")
  const [selectedReaderId, setSelectedReaderId] = useState<string>("")

  // Queries pour récupérer les données
  const { readers = [], isLoading: loadingReaders } = useStripeReadersQuery()
  const { cashRegisters, pendingCashRegisters: loadingCashRegisters } =
    useCashRegistersQuery()

  // Filtrer les terminaux disponibles
  const usedReaderIds = cashRegisters.map(
    (cr: CashRegisterTable) => cr.readerId
  )
  const availableReaders = readers.filter(
    (reader) => !usedReaderIds.includes(reader.id)
  )
  const unavailableReaders = readers.filter((reader) =>
    usedReaderIds.includes(reader.id)
  )

  const handleAddCashRegister = async () => {
    try {
      const userAttributes = await fetchUserAttributes()
      const shopId = userAttributes["custom:shopId"]

      if (!shopId) {
        throw new Error("Shop ID not found")
      }

      // Validation des champs obligatoires
      if (!selectedReaderId) {
        addToast({
          type: "error",
          title: "Terminal requis",
          description: "Veuillez sélectionner un terminal disponible",
        })
        return
      }

      // Créer la caisse avec le readerId sélectionné
      await addCashRegisterMutation.mutateAsync({
        name: cashRegisterName || undefined,
        status: cashRegisterStatus,
        readerId: selectedReaderId,
      })

      // Réinitialiser le formulaire et fermer le modal
      handleClose()
    } catch (error) {
      console.error("Erreur lors de l'ajout de la caisse:", error)
      // Les erreurs spécifiques sont gérées par les mutations
    }
  }

  const handleClose = () => {
    if (!addCashRegisterMutation.isPending) {
      setCashRegisterName("")
      setCashRegisterStatus("active")
      setSelectedReaderId("")
      onClose()
    }
  }

  const handleOpenManageReaders = () => {
    handleClose()
    onOpenManageReaders()
  }

  const getStatusIcon = (status: string) => {
    return status === "online" ? (
      <Wifi className="h-4 w-4 text-green-500" />
    ) : (
      <WifiOff className="h-4 w-4 text-red-500" />
    )
  }

  const getStatusLabel = (status: string) => {
    return status === "online" ? "En ligne" : "Hors ligne"
  }

  const isLoading =
    addCashRegisterMutation.isPending || loadingReaders || loadingCashRegisters

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Terminal className="h-5 w-5 text-indigo-600" />
            <DialogTitle>Ajouter une nouvelle caisse</DialogTitle>
          </div>
          <DialogDescription>
            Créez une nouvelle caisse enregistreuse en sélectionnant un terminal
            disponible.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Section Caisse */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Informations de la caisse</h3>

            <div className="space-y-2">
              <Label htmlFor="cashRegisterName">
                Nom de la caisse (optionnel)
              </Label>
              <Input
                id="cashRegisterName"
                value={cashRegisterName}
                onChange={(e) => setCashRegisterName(e.target.value)}
                placeholder="Ex: Caisse principale, Caisse 1..."
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cashRegisterStatus">Statut initial</Label>
              <Select
                value={cashRegisterStatus}
                onValueChange={(value: "active" | "inactive") =>
                  setCashRegisterStatus(value)
                }
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Section Terminal */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Sélectionner un terminal</h3>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2">Chargement des terminaux...</span>
              </div>
            ) : availableReaders.length === 0 ? (
              <div className="text-center space-y-4 ">
                <div className="bg-blue-50 p-4 rounded-lg justify-center items-center flex flex-col">
                  <Info className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                  <h4 className="font-medium text-blue-900 mb-2">
                    Aucun terminal disponible
                  </h4>
                  <p className="text-sm text-blue-700 mb-4">
                    Tous vos terminaux sont déjà associés à des caisses ou vous
                    n'avez pas encore de terminal enregistré.
                  </p>
                  <Button
                    onClick={handleOpenManageReaders}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Ajouter un nouveau terminal
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Sélectionnez un terminal disponible pour cette caisse :
                </p>

                {/* Terminaux disponibles */}
                <div className="space-y-2">
                  {availableReaders.map((reader) => (
                    <div
                      key={reader.id}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        selectedReaderId === reader.id
                          ? "border-indigo-500 bg-indigo-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => setSelectedReaderId(reader.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(reader.status!)}
                          <div>
                            <p className="font-medium">{reader.label}</p>
                            <p className="text-sm text-gray-500">
                              {getStatusLabel(reader.status!)} • N/S:{" "}
                              {reader.serial_number}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            name="selectedReader"
                            value={reader.id}
                            checked={selectedReaderId === reader.id}
                            onChange={() => setSelectedReaderId(reader.id)}
                            className="h-4 w-4 text-indigo-600"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Terminaux non disponibles */}
                {unavailableReaders.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500 font-medium">
                      Terminaux déjà utilisés :
                    </p>
                    {unavailableReaders.map((reader) => (
                      <div
                        key={reader.id}
                        className="border rounded-lg p-3 bg-gray-50 opacity-60"
                      >
                        <div className="flex items-center gap-3">
                          {getStatusIcon(reader.status!)}
                          <div>
                            <p className="font-medium">{reader.label}</p>
                            <p className="text-sm text-gray-500">
                              {getStatusLabel(reader.status!)} • N/S:{" "}
                              {reader.serial_number} • Déjà utilisé
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button
            onClick={handleAddCashRegister}
            disabled={isLoading || !selectedReaderId}
            className="flex items-center gap-2"
          >
            {addCashRegisterMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Création en cours...
              </>
            ) : (
              "Créer la caisse"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
